import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { canAccessSection } from "../constants/contentSections";
import LanguageSwitcher from "./LanguageSwitcher";
import { useT } from "../i18n";

interface SidebarProps {
  // Mobile drawer state. On lg+ the sidebar is always shown regardless of these.
  open?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open = false, onClose }) => {
  const { logout, user } = useAuth();
  const t = useT();

  const handleLogout = () => {
    logout();
  };

  // Close the mobile drawer after tapping a nav item
  const handleNavClick = () => {
    onClose?.();
  };

  // Marketers only get the content sections granted on their account;
  // Users and Deals are admin-only (they carry no section key).
  const can = (sectionKey?: string) => canAccessSection(user as any, sectionKey);

  return (
    <>
      {/* Backdrop — mobile only, when drawer is open */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`w-72 bg-[#111827] text-gray-300 flex flex-col h-screen fixed top-0 left-0 z-40 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
      <div
        id="sidebar-header"
        className="flex items-center justify-start h-20 px-6 border-b border-gray-700/50"
      >
        <div className="p-2 bg-white/10 rounded-md mr-3">
          <img
            src="/luxia.svg"
            alt={t("layout.logoAlt")}
            className="h-8 w-8 object-contain"
          />
        </div>
        <h1 className="text-xl font-bold text-white">{t("layout.portalName")}</h1>
      </div>
      <nav id="sidebar-nav" className="flex-grow pt-6 px-4">
        <ul className="space-y-1">
          {can() && (
          <li>
            <NavLink
              to="/"
              onClick={handleNavClick}
              end
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg ${
                  isActive
                    ? "bg-gray-50 text-gray-900 font-semibold shadow-sm"
                    : "hover:bg-gray-700/50 transition-colors"
                } cursor-pointer`
              }
            >
              {({ isActive }) => (
                <>
                  <i
                    className={`fa-solid fa-users w-6 text-center mr-2 text-lg ${
                      isActive ? "text-gray-800" : ""
                    }`}
                  ></i>
                  {t("layout.sidebar.users")}
                </>
              )}
            </NavLink>
          </li>
          )}
          {can() && (
          <li>
            <NavLink
              to="/deals"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg ${
                  isActive
                    ? "bg-gray-50 text-gray-900 font-semibold shadow-sm"
                    : "hover:bg-gray-700/50 transition-colors"
                } cursor-pointer`
              }
            >
              <i className="fa-solid fa-handshake w-6 text-center mr-2 text-lg"></i>
              {t("layout.sidebar.deals")}
            </NavLink>
          </li>
          )}
          {can("releases") && (
          <li>
            <NavLink
              to="/releases"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg ${
                  isActive
                    ? "bg-gray-50 text-gray-900 font-semibold shadow-sm"
                    : "hover:bg-gray-700/50 transition-colors"
                } cursor-pointer`
              }
            >
              <i className="fa-solid fa-rocket w-6 text-center mr-2 text-lg"></i>
              {t("layout.sidebar.releases")}
            </NavLink>
          </li>
          )}
          {can("product-sheets") && (
          <li>
            <NavLink
              to="/product-sheet"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg ${
                  isActive
                    ? "bg-gray-50 text-gray-900 font-semibold shadow-sm"
                    : "hover:bg-gray-700/50 transition-colors"
                } cursor-pointer`
              }
            >
              <i className="fa-solid fa-file-lines w-6 text-center mr-2 text-lg"></i>
              {t("layout.sidebar.productSheets")}
            </NavLink>
          </li>
          )}
          {can("documents") && (
          <li>
            <NavLink
              to="/documents"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg ${
                  isActive
                    ? "bg-gray-50 text-gray-900 font-semibold shadow-sm"
                    : "hover:bg-gray-700/50 transition-colors"
                } cursor-pointer`
              }
            >
              <i className="fa-solid fa-folder-open w-6 text-center mr-2 text-lg"></i>
              {t("layout.sidebar.documents")}
            </NavLink>
          </li>
          )}
          {can("brand-guide") && (
          <li>
            <NavLink
              to="/brand-guide"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg ${
                  isActive
                    ? "bg-gray-50 text-gray-900 font-semibold shadow-sm"
                    : "hover:bg-gray-700/50 transition-colors"
                } cursor-pointer`
              }
            >
              <i className="fa-solid fa-palette w-6 text-center mr-2 text-lg"></i>
              {t("layout.sidebar.brandGuide")}
            </NavLink>
          </li>
          )}
          {can("graphics") && (
          <li>
            <NavLink
              to="/graphics"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg ${
                  isActive
                    ? "bg-gray-50 text-gray-900 font-semibold shadow-sm"
                    : "hover:bg-gray-700/50 transition-colors"
                } cursor-pointer`
              }
            >
              <i className="fa-solid fa-images w-6 text-center mr-2 text-lg"></i>
              {t("layout.sidebar.graphics")}
            </NavLink>
          </li>
          )}
          {can("events") && (
          <li>
            <NavLink
              to="/events"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg ${
                  isActive
                    ? "bg-gray-50 text-gray-900 font-semibold shadow-sm"
                    : "hover:bg-gray-700/50 transition-colors"
                } cursor-pointer`
              }
            >
              <i className="fa-solid fa-calendar-plus w-6 text-center mr-2 text-lg"></i>
              {t("layout.sidebar.events")}
            </NavLink>
          </li>
          )}
          {can("academy") && (
          <li>
            <NavLink
              to="/academy"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg ${
                  isActive
                    ? "bg-gray-50 text-gray-900 font-semibold shadow-sm"
                    : "hover:bg-gray-700/50 transition-colors"
                } cursor-pointer`
              }
            >
              <i className="fa-solid fa-graduation-cap w-6 text-center mr-2 text-lg"></i>
              {t("layout.sidebar.academy")}
            </NavLink>
          </li>
          )}
          {can() && (
          <li>
            <NavLink
              to="/language"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center px-4 py-2.5 rounded-lg ${
                  isActive
                    ? "bg-gray-50 text-gray-900 font-semibold shadow-sm"
                    : "hover:bg-gray-700/50 transition-colors"
                } cursor-pointer`
              }
            >
              <i className="fa-solid fa-language w-6 text-center mr-2 text-lg"></i>
              {t("layout.sidebar.language")}
            </NavLink>
          </li>
          )}
        </ul>
      </nav>
      <div id="sidebar-footer" className="p-4 border-t border-gray-700/50">
        <div className="space-y-1">
          <LanguageSwitcher />
          <span className="flex items-center px-4 py-2.5 rounded-lg hover:bg-gray-700/50 transition-colors cursor-pointer">
            <i className="fa-solid fa-user w-6 text-center mr-2 text-lg"></i>
            {t("layout.sidebar.profile")}
          </span>
          <span
            onClick={handleLogout}
            className="flex items-center px-4 py-2.5 rounded-lg hover:bg-gray-700/50 transition-colors cursor-pointer"
          >
            <i className="fa-solid fa-arrow-right-from-bracket w-6 text-center mr-2 text-lg"></i>
            {t("layout.sidebar.logout")}
          </span>
        </div>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
